<template>
  <v-dialog
    :model-value="isOpen"
    @update:model-value="handleClose"
    max-width="800px"
    scrim="rgba(0,0,0,0.65)"
    transition="dialog-bottom-transition"
  >
    <v-card class="rounded-2xl">
      <v-card-title class="py-3 d-flex align-center">
        <v-icon class="mr-2">mdi-history</v-icon>
        <span class="text-h6">Line History</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading history...</p>
        </div>

        <div v-else-if="error" class="text-center py-4">
          <v-icon color="error" size="48">mdi-alert-circle</v-icon>
          <p class="error--text mt-2">{{ error }}</p>
          <v-btn @click="fetchHistory" color="primary" outlined>
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </div>

        <div v-else-if="historyEntries.length === 0" class="text-center py-4">
          <v-icon color="grey" size="48">mdi-history</v-icon>
          <p class="grey--text mt-2">No history available for this line</p>
        </div>

        <div v-else>
          <div class="mb-4">
            <h4>Current Content:</h4>
            <div class="current-content pa-3 mb-3">
              {{ currentContent || '(empty)' }}
            </div>
          </div>

          <h4>History:</h4>
          <v-timeline dense>
            <v-timeline-item
              v-for="(entry, index) in processedHistoryEntries"
              :key="entry.id"
              :color="getTimelineColor(index)"
              small
            >
              <template v-slot:opposite>
                <div class="text-caption">
                  {{ formatDateTime(entry.created_at) }}
                </div>
              </template>

              <v-card class="elevation-2 mb-2">
                <v-card-text class="pb-2">
                  <div class="d-flex align-center mb-2">
                    <v-icon class="mr-1" :color="getTimelineColor(index)">mdi-pencil</v-icon>
                    <span class="font-weight-medium">
                      {{ entry.changed_by_username || 'Unknown user' }}
                    </span>
                  </div>

                  <!-- Diff Display -->
                  <div class="diff-container">
                    <div class="diff-section">
                      <div class="diff-label">
                        <v-icon small color="error">mdi-minus</v-icon>
                        <span class="text-caption ml-1">Removed:</span>
                      </div>
                      <div class="diff-content removed pa-2">
                        {{ entry.removedDiff || '(no removed content)' }}
                      </div>
                    </div>

                    <div class="diff-section mt-2">
                      <div class="diff-label">
                        <v-icon small color="success">mdi-plus</v-icon>
                        <span class="text-caption ml-1">Added:</span>
                      </div>
                      <div class="diff-content added pa-2">
                        {{ entry.addedDiff || '(no added content)' }}
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-end mt-2">
                    <v-btn
                      small
                      color="primary"
                      outlined
                      @click="revertToVersion(entry.old_text_content)"
                    >
                      <v-icon small left>mdi-restore</v-icon>
                      Revert to this version
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </div>
      </v-card-text>
    </v-card>

    <!-- Confirmation Dialog for Revert -->
    <v-dialog
      :model-value="revertDialog"
      @update:model-value="revertDialog = $event"
      max-width="400px"
      scrim="rgba(0,0,0,0.65)"
      transition="dialog-bottom-transition"
    >
      <v-card class="rounded-2xl">
        <v-card-title class="py-3">Confirm Revert</v-card-title>
        <v-card-text>
          Are you sure you want to revert to this version? This will replace the current content.
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="revertDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmRevert">Revert</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "@/plugins/axios";

export default {
  name: 'LineHistoryDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    lineId: {
      type: [Number, String],
      required: true
    },
    projectId: {
      type: [Number, String],
      required: true
    },
    documentId: {
      type: [Number, String],
      required: true
    },
    pageId: {
      type: [Number, String],
      required: true
    },
    currentContent: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      historyEntries: [],
      revertDialog: false,
      revertContent: '',
      diffCache: new Map() // Cache diff calculations
    };
  },
  computed: {
    // Pre-compute all diffs to avoid recalculation during render
    processedHistoryEntries() {
      return this.historyEntries.map(entry => {
        const cacheKey = `${entry.id}-${entry.old_text_content}-${entry.new_text_content}`;

        if (!this.diffCache.has(cacheKey)) {
          const removedDiff = this.generateSimpleDiff(entry.old_text_content, entry.new_text_content, 'removed');
          const addedDiff = this.generateSimpleDiff(entry.old_text_content, entry.new_text_content, 'added');

          this.diffCache.set(cacheKey, {
            removedDiff,
            addedDiff
          });
        }

        return {
          ...entry,
          ...this.diffCache.get(cacheKey)
        };
      });
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.fetchHistory();
      }
    }
  },
  methods: {
    handleClose(value) {
      // Called when dialog is closed by clicking outside or pressing ESC
      if (!value) {
        this.closeDialog();
      }
    },

    async fetchHistory() {
      if (!this.lineId || !this.projectId || !this.documentId || !this.pageId) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await api.get(
          `/api/projects/${this.projectId}/documents/${this.documentId}/pages/${this.pageId}/lines/${this.lineId}/history/`
        );
        this.historyEntries = response.data;
      } catch (error) {
        console.error('Error fetching line history:', error);
        this.error = error.response?.data?.error || 'Failed to load history';
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      // Clear immediately for fast close
      this.diffCache.clear();
      this.historyEntries = [];
      this.error = null;
      this.$emit('close');
    },

    formatDateTime(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getTimelineColor(index) {
      const colors = ['primary', 'secondary', 'accent', 'info', 'warning'];
      return colors[index % colors.length];
    },

    generateSimpleDiff(oldText, newText, type) {
      if (!oldText) oldText = '';
      if (!newText) newText = '';

      if (type === 'removed') {
        // Show what was in old but not in new
        return oldText === newText ? '' : oldText;
      } else {
        // Show what's in new but not in old
        return oldText === newText ? '' : newText;
      }
    },

    revertToVersion(content) {
      this.revertContent = content;
      this.revertDialog = true;
    },

    confirmRevert() {
      this.revertDialog = false;
      this.$emit('revert', this.revertContent);
      this.closeDialog();
    }
  }
};
</script>

<style scoped>
.current-content {
  background-color: rgb(var(--v-theme-surface-variant));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

.diff-container {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  overflow: hidden;
}

.diff-section {
  background-color: rgb(var(--v-theme-surface));
}

.diff-label {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background-color: rgb(var(--v-theme-surface-variant));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.diff-content {
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 24px;
  border-left: 3px solid transparent;
}

.diff-content.removed {
  background-color: #ffebee;
  border-left-color: #f44336;
}

.diff-content.added {
  background-color: #e8f5e8;
  border-left-color: #4caf50;
}

.diff-highlight {
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 500;
}

.diff-highlight.diff-removed {
  background-color: #ffcdd2;
  color: #c62828;
}

.diff-highlight.diff-added {
  background-color: #c8e6c9;
  color: #2e7d32;
}

.v-timeline-item {
  padding-bottom: 16px;
}
</style>
